/*
    1. wizard formular obalit do <v-form @input="updateFormValid" ref="form">
    2. na wizard form pridat do data defaultMessage s default hlaskou 
      defaultMessage: {
        text: '',
        isError: false,
      }
    3. pridat metodu reload(), v nej bude zabezpecene aby sa nacitali dynamicke kontrolky
    4. pridat watch kde kazda kontrolka si bude porovnavat svoj nazov
            currentControl(val) {
                if (val === 'PripadyWizardNajomca') this.reload();
            },
    5. ak chceme zobrazit message na snackbare, pouzijeme globalny snack bar
            eventBus.$emit('snack-error', 'Chybova hlaska');
            eventBus.$emit('snack-info', 'Info hlaska');

    AKO POUZIT validationObject
    ak potrebujeme na formulari pridat nejaku kontrolu, napr. overit nieco v DB, a tato kontrolka ma zamedzit pokracovaniu vo formulari
    vysedok tejto kontroly pridame do objektu validationObject tak, ze pridame novu property pomocou this.$set(this.validationObject, 'unikatnyNazovPropery', true/false);

    validationObject ma dynamicke property, kazda properta je objekt, priklad:
    validationObject: {
      dynProp: {
        form: 'PripadyWizardNajomca
        value: true
      },
      dynProp2: {
        form: 'PripadyWizardPredmet',
        value: false
      }
    }    

    validationChanged event ma strukturu:
    {
        message: {
          message: sprava,
          isError: ak je true, cerveny text
        }
        valid: true/false na pokracovat,
        controlName: this.controlName,
        showProgress: zobrazenie loadingu,
    }
*/
import { mapState, mapMutations, mapGetters } from 'vuex';
import { eventBus } from '../main.js';
import DebugControl from '../components/basic/WizDebugControl.vue';
import protectedAxios from '../auth/axios.js';
import { cacheMixin } from './cacheMixin.js';


export const wizardMixin = {
  mixins: [cacheMixin],
  components: {
    DebugControl,
  },
  data() {
    return {
      formValid: true,
      formDirty: false,
      loadingMessage: 'Priebieha načítavanie dát, prosím počkajte ...',
      wizardMessage: {
        text: '',
        isError: false,
      },
      validationObject: {},
      validationObjectResult: true,
      wizardMessageProgress: false,
      wizardBlocked: false, //nevie sa pohybovat ani dopredu ani dozadu na stepperi
      reloaded: false,
      showOverlay: true,
      showOverlayTimeout: null,
      sendFormControlsLoadedTimeout: null,
      onceLoaded: false, //sem sa zapise flag, ked prvy krat vsetky konrolky budu mat stav isLoading === false
    };
  },
  provide() {
    return {
      wizardForm: this,
    };
  },
  props: {
    currentControl: String,
    controlName: String,
    title: String,
  },
  computed: {
    ...mapState(['pripad', 'wizardFormControls','isMobile']),
    ...mapGetters(['debug']),
    finalValidationResult() {
      return (
        this.validationObjectResult && !this.somethingIsLoading //&& this.formValid
      );
    },
    formControlsLoaded() {
      return this.wizardFormControls
        .filter(c => c.dataId && c.formName === this.controlName)
        .map(c => c.initComplete)
        .reduce((a, v) => a && v, true);
    },
    somethingIsLoading() {
      return this.wizardFormControls
        .filter(c => c.dataId && c.formName === this.controlName)
        .map(c => c.isLoading)
        .reduce((a, v) => a || v, false);
    },
    somethingGetDirty() {
      return this.wizardFormControls
        .filter(c => c.dataId && c.formName === this.controlName)
        .map(c => c.dirty)
        .reduce((a, v) => a || v, false);
    },
  },
  watch: {
    wizardMessage() {
      this.emitFinalValid(this.finalValidationResult);
    },
    finalValidationResult(val) {
      this.emitFinalValid(val);
    },
    validationObject: {
      handler(val) {
        this.validationObjectResult = Object.entries(this.validationObject).reduce(
          (a, [_, v]) => a && v,
          true
        );
      },
      deep: true,
    },
    currentControl(val) {
      if (val === this.controlName) {
        this.wizardMessage = this.defaultMessage;
        this.emitFinalValid(this.finalValidationResult);
      }
    },
    somethingGetDirty(val) {
      this.emitDirty(val);
    },
    somethingIsLoading(val) {
      clearTimeout(this.showOverlayTimeout);
      clearTimeout(this.sendFormControlsLoadedTimeout);
      //ak sa nieco stihne nacitat/ulozit do 300ms, tak sa nemusi zobrazovat overlay
      this.showOverlayTimeout = setTimeout(() => {
        this.$emit('somethingIsLoadingChanged', val);

        if (!val) {
          this.sendFormControlsLoadedTimeout = setTimeout(() => {
            //ak sa selectne predmet, vyvolam nacitanie dalsich tabov
              this.$emit('formControlsLoaded');
          }, 700);
        }
      }, 300);
    },
  },
  methods: {
    ...mapMutations(['setHandleExit']),
    emitDirty(val) {
      if(val){
        this.formDirty = true;
        this.setHandleExit(true);
      }
      this.$emit('dirtyChanged', val);
    },
    setCustomButton(visible, enabled, text, onClick) {
      this.$emit('setCustomButton', {
        visible: visible,
        enabled: enabled,
        text: text,
        onClick: onClick,
      });
    },
    setCustomButtonSecondary(visible, text, onClick) {
      this.$emit('setCustomButtonSecondary', {
        visible: visible,
        text: text,
        onClick: onClick,
      });
    },
    clearCustomButton() {
      this.$emit('setCustomButton', {
        visible: false,
        enabled: false,
        text: '',
        onClick: () => {},
      });
    },
    clearDirty() {
      eventBus.$emit('resetControlDirty', this.controlName);
    },
    //nastavi validacnu hodnotu do objektu validationObject
    setValid(value, property, message, showProgress, isError, isBlocked = false) {
      this.wizardMessageProgress = showProgress;
      this.wizardMessage = { text: message, isError };
      this.wizardBlocked = isBlocked;
      this.$set(this.validationObject, property, value);
    },
    async validateForm() {
      const isValid = await this.$refs.form.validate();
      return isValid;
    },
    updateFormValid(val) {
      this.formValid = val;
    },
    emitFinalValid(val) {
      if (this.currentControl === this.controlName) {
        this.$emit('validationChanged', {
          message: this.wizardMessage,
          valid: val,
          blocked: this.wizardBlocked,
          controlName: this.controlName,
          showProgress: this.wizardMessageProgress,
        });
      }
    },
    log() {
      console.log(
        this.controlName,
        this.wizardFormControls
          .filter(c => c.formName === this.controlName)
          //.filter(c => c.dirty === true)
          .map(c => {
            return {
              dataId: c.dataId,
              cisloPredmetu: c.cisloPredmetu,
              initComplete: c.initComplete,
              dirty: c.dirty,
              isLoading: c.isLoading,
            };
          })
      );
    },
    async checkCacheChanged(){
      return await protectedAxios
      .get('/controls/checkCacheChanged/' + this.pripad.newId)
      .then(res => {
        return res.data == 1 ? true : false
      });
    }
  },
  beforeMount() {
    this.wizardMessage = this.defaultMessage;
  },
  mounted() {
    if (!this.reloaded && this.reload) {
      this.reload();
      this.reloaded = true;
    }
  },
};
